import { useEffect, useState } from 'react';
import { Button, Input, DataTable, Column, Badge, IconButton, useToast, ConfirmDialog, Modal, EmptyState } from '../../../shared/components';
import { PlusIcon, EditIcon, TrashIcon } from '../../../shared/icons';
import { getHumanResourcesRepository } from '../../../shared/services/humanResources/repositoryFactory';
import { TemporalCompany } from '../../../shared/services/humanResources/types';

const repo = getHumanResourcesRepository();

/**
 * Parámetros > Empresas Temporales: catálogo que alimenta el selector
 * "Temporal" del formulario de Empleados (antes era texto libre).
 */
export function TemporalCompaniesTab() {
  const toast = useToast();
  const [rows, setRows] = useState<TemporalCompany[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<TemporalCompany | null>(null);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deleting, setDeleting] = useState<TemporalCompany | null>(null);

  const load = () => {
    setIsLoading(true);
    repo.listTemporalCompanies(true).then((r) => {
      setRows(r);
      setIsLoading(false);
    });
  };
  useEffect(load, []);

  const openCreate = () => {
    setName('');
    setCreating(true);
  };
  const openEdit = (r: TemporalCompany) => {
    setName(r.name);
    setEditing(r);
  };
  const close = () => {
    setCreating(false);
    setEditing(null);
  };
  const save = async () => {
    if (!name.trim()) {
      toast.show('El nombre es obligatorio.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      if (editing) {
        await repo.updateTemporalCompany(editing.id, { name });
        toast.show('Empresa temporal actualizada.');
      } else {
        await repo.createTemporalCompany({ name, isActive: true });
        toast.show('Empresa temporal agregada.');
      }
      close();
      load();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'No se pudo guardar.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (r: TemporalCompany) => {
    if (r.isActive) {
      await repo.deleteTemporalCompany(r.id);
    } else {
      await repo.updateTemporalCompany(r.id, { isActive: true });
    }
    load();
  };

  const columns: Column<TemporalCompany>[] = [
    { key: 'name', header: 'Nombre', render: (r) => r.name, sortable: true, sortValue: (r) => r.name },
    {
      key: 'activo',
      header: 'Estado',
      render: (r) => (
        <button onClick={() => toggleActive(r)}>
          <Badge tone={r.isActive ? 'success' : 'neutral'}>{r.isActive ? 'Activo' : 'Inactivo'}</Badge>
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex gap-1">
          <IconButton icon={<EditIcon width={16} height={16} />} label="Editar" onClick={() => openEdit(r)} className="h-9 w-9 text-ink-500 hover:bg-ink-100" />
          <IconButton icon={<TrashIcon width={16} height={16} />} label="Eliminar" onClick={() => setDeleting(r)} className="h-9 w-9 text-critical hover:bg-red-50" />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Button leftIcon={<PlusIcon width={18} height={18} />} onClick={openCreate}>
        Agregar empresa temporal
      </Button>

      {rows.length === 0 && !isLoading ? (
        <EmptyState icon="🏢" title="Sin empresas temporales" description="Agrega la primera para que aparezca en el selector 'Temporal' de Empleados." />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} isLoading={isLoading} pageSize={10} pageSizeOptions={[10, 25, 50]} emptyTitle="Sin empresas temporales" />
      )}

      <Modal open={creating || !!editing} onClose={close} title={editing ? 'Editar empresa temporal' : 'Agregar empresa temporal'}>
        <div className="space-y-3">
          <Input label="Nombre" placeholder="Ej. OCUPAR" value={name} onChange={(e) => setName(e.target.value)} />
          <div className="flex gap-3 pt-2">
            <Button variant="outline" fullWidth onClick={close}>Cancelar</Button>
            <Button fullWidth onClick={save} isLoading={isSaving}>Guardar</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        title="Desactivar empresa temporal"
        description={`¿Desactivar "${deleting?.name}"? Dejará de aparecer en el selector de Empleados, pero los empleados ya asignados a ella no se ven afectados.`}
        tone="danger"
        confirmLabel="Desactivar"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await repo.deleteTemporalCompany(deleting.id);
          setDeleting(null);
          load();
        }}
      />
    </div>
  );
}
